/**
 * What THIS device's GPU can render — the one question every world list asks before it offers a world,
 * answered by the ENGINE (engineApi 1.17): `probeCapabilities()` once per page, and each world's needs from
 * `worldNeeds` (its family's implied needs merged with its declared World.needs — the fx generator writes
 * fxShaderNeeds(shader) into every fx World). One source: the same caps object filters the pools here,
 * the Director (DirectorOptions.capabilities) and the host's PostFX decision (VisualHostOptions.capabilities).
 *
 * What it means in practice (SDK correction, v0.18.4): the engine's shader scenes and PostFX are GLSL ES
 * 1.00 and run on WebGL1; only three.js worlds (three r170 dropped WebGL1) and fx shaders using LOD lookups /
 * ES 3.00 need WebGL2. A WebGL1-only panel (some Tizen 2020–21 sets) keeps the shader scenes, the fx visuals
 * and PostFX — it loses the three.js worlds only. (Art's own probe before this treated WebGL1 as "fx only,
 * PostFX off", which over-filtered.)
 */
import type { GpuCapabilities, World } from '@syqel/engine'
import { Engine } from '@syqel/ota-client'

let caps: GpuCapabilities | null = null

/** This host's GPU capabilities (probed once, cached by the engine too). */
export function hostCapabilities(): GpuCapabilities {
  if (!caps) {
    caps = Engine.probeCapabilities()
    if (caps.probed && !caps.webgl2) console.warn(`[syqel] no WebGL2 — ${caps.webgl1 ? 'the three.js worlds are left out' : 'no WebGL at all: 2D worlds only'}`)
  }
  return caps
}

/** Can this device render `world`? (A not-probed host — no canvas — keeps everything.) */
export function worldRenderable(world: Pick<World, 'mode'> & Partial<Pick<World, 'fx' | 'needs' | 'threeId'>>): boolean {
  return Engine.worldSupported(world, hostCapabilities())
}
